/**
 * Recording that a practice exercise was completed.
 *
 * Deliberately narrow. A practice session is written when a learner *finishes an
 * exercise* — checks a guided-ink answer, ends an ear-training run, marks a
 * creative task done. It is not written for a tap, a stroke, a tool change, a
 * page view or a lesson being opened. Those would be behavioural telemetry
 * dressed up as learning data, and the brief rules them out explicitly.
 *
 * What is stored is the minimum that answers "what did they practise, and how
 * did it go": the feature, the lesson, which task, and the outcome. Never the
 * handwriting, never the answer given, never the wrong option chosen, never a
 * timestamp per interaction. The result payload is a fixed small shape rather
 * than a free-form bag, so it cannot quietly grow into a tracker.
 *
 * Each completion is its own append-only record with its own UUID, which is what
 * makes syncing it idempotent: replaying the upload cannot create a second copy,
 * and the server never rewrites one that exists.
 */

import { repositories } from '../storage/repositories';
import { freshMeta } from '../storage/types';

/** The three exercises a lesson actually contains. */
export type PracticeTask = 'guided-ink' | 'ear-training' | 'creative';

export type PracticeOutcome =
  /** The guided-ink answer matched, or an ear run reached the lesson's target. */
  | 'passed'
  /** The attempt was completed but did not meet the goal. */
  | 'missed'
  /** A creative task the learner marked done; there is nothing to grade. */
  | 'completed';

export interface PracticeRecord {
  lessonId: string;
  task: PracticeTask;
  outcome: PracticeOutcome;
  /** For an ear run: how many in a row were right. Absent for other tasks. */
  correctInRun?: number;
  /** When the exercise began, so a session has a duration rather than a point. */
  startedAt: number;
}

/**
 * Append one completed exercise.
 *
 * Failures are swallowed on purpose: a practice record is a nice-to-have
 * summary, and a storage problem must never interrupt or undo the learning that
 * just happened. The mastery record — the thing that actually matters — is
 * written separately and does surface its failures.
 */
export async function recordPractice(entry: PracticeRecord): Promise<void> {
  try {
    const now = Date.now();
    await repositories().sessions.append({
      ...freshMeta(now),
      feature: 'learn',
      lessonId: entry.lessonId,
      result: {
        task: entry.task,
        outcome: entry.outcome,
        ...(entry.correctInRun === undefined ? {} : { correctInRun: entry.correctInRun }),
      },
      startedAt: entry.startedAt,
      completedAt: now,
    });
  } catch {
    /* see above */
  }
}
